import React from 'react';
import { Chip, Box } from '@mui/material';
import PropTypes from 'prop-types';

const StatusBadge = ({ status }) => {
  const getStatusColor = (status) => {
    if (!status) return 'default';
    
    const statusLower = status.toLowerCase();
    
    // Handle enum values
    switch (statusLower) {
      case 'prospect':
        return 'default';
      case 'new':
        return 'primary'; // Blue
      
      // Quote statuses
      case 'quote':
      case 'quoting':
        return 'warning'; // Light Orange
      case 'quote active':
        return 'warning'; // Orange (will customize with sx)
      case 'quote needs changes':
      case 'changes needed':
        return 'warning'; // Dark Orange (will customize with sx)
      case 'quote declined':
        return 'error'; // Red
      case 'quote accepted':
      case 'quote approved':
        return 'success'; // Green
      
      // Design statuses
      case 'in design':
        return 'warning'; // Yellow (will customize with sx)
      
      // Proof statuses
      case 'proof':
      case 'proofing':
        return 'secondary'; // Light Purple (will customize with sx)
      case 'proof active':
        return 'secondary'; // Purple (will customize with sx)
      case 'proof needs changes':
        return 'secondary'; // Dark Purple (will customize with sx)
      case 'proof pending':
      case 'proof_pending':
        return 'secondary';
      case 'proof accepted':
      case 'proof approved':
        return 'success'; // Green
      
      // Production statuses
      case 'in production':
      case 'in_production':
        return 'warning'; // Yellow
      case 'invoiced':
        return 'success'; // Green
      
      // General statuses
      case 'qualified':
      case 'pending':
        return 'warning';
      case 'proposal':
        return 'info';
      case 'negotiation':
        return 'primary';
      case 'closed_won':
      case 'approved':
      case 'completed':
        return 'success';
      case 'closed_lost':
      case 'rejected':
      case 'cancelled':
        return 'error'; // Red
      case 'waiting for artwork':
        return 'warning';
      case 'uploaded':
        return 'info';
      case 'draft':
        return 'default';
      case 'on hold':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getCustomStyles = (status) => {
    if (!status) return {};
    
    const statusLower = status.toLowerCase();
    const baseStyles = { fontWeight: 'medium' };
    
    // Custom colors for specific statuses that need exact shades
    switch (statusLower) {
      case 'quote':
      case 'quoting':
        return { ...baseStyles, bgcolor: '#FFB74D', color: '#000' }; // Light Orange
      case 'quote active':
        return { ...baseStyles, bgcolor: '#FF9800', color: '#000' }; // Orange
      case 'quote needs changes':
      case 'changes needed':
        return { ...baseStyles, bgcolor: '#F57C00', color: '#fff' }; // Dark Orange
      case 'in design':
        return { ...baseStyles, bgcolor: '#FDD835', color: '#000' }; // Yellow
      case 'proof':
      case 'proofing':
        return { ...baseStyles, bgcolor: '#CE93D8', color: '#000' }; // Light Purple
      case 'proof active':
        return { ...baseStyles, bgcolor: '#9C27B0', color: '#fff' }; // Purple
      case 'proof needs changes':
        return { ...baseStyles, bgcolor: '#6A1B9A', color: '#fff' }; // Dark Purple
      case 'in production':
      case 'in_production':
        return { ...baseStyles, bgcolor: '#FDD835', color: '#000' }; // Yellow
      default:
        return baseStyles;
    }
  };

  const getStatusLabel = (status) => {
    if (!status) return 'Pending';
    
    const statusLower = status.toLowerCase();
    
    // Handle enum values with friendly names
    switch (statusLower) {
      case 'prospect':
        return 'New';
      case 'quote':
      case 'quoting':
        return 'Quote';
      case 'quote active':
        return 'Quote Active';
      case 'quote needs changes':
      case 'changes needed':
        return 'Quote Needs Changes';
      case 'quote declined':
        return 'Quote Declined';
      case 'quote accepted':
      case 'quote approved':
        return 'Quote Accepted';
      case 'qualified':
        return 'Qualified';
      case 'proposal':
        return 'Proposal';
      case 'negotiation':
        return 'Negotiation';
      case 'closed_won':
        return 'Won';
      case 'closed_lost':
        return 'Lost';
      case 'waiting for artwork':
        return 'Waiting For Artwork';
      case 'in design':
        return 'In Design';
      case 'proof':
      case 'proofing':
        return 'Proof';
      case 'proof active':
        return 'Proof Active';
      case 'proof needs changes':
        return 'Proof Needs Changes';
      case 'proof pending':
      case 'proof_pending':
        return 'Proof Pending';
      case 'proof accepted':
      case 'proof approved':
        return 'Proof Accepted';
      case 'in production':
      case 'in_production':
        return 'In Production';
      case 'approved':
        return 'Approved';
      case 'invoiced':
        return 'Invoiced';
      case 'cancelled':
        return 'Cancelled';
      default:
        // For any other status, capitalize the first letter of each word
        return status
          .split(/[_\s]/)
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
    }
  };

  return (
    <Box display="inline-block">
      <Chip 
        label={getStatusLabel(status)} 
        color={getStatusColor(status)}
        size="small"
        sx={getCustomStyles(status)}
      />
    </Box>
  );
};

StatusBadge.propTypes = {
  status: PropTypes.string
};

StatusBadge.defaultProps = {
  status: 'prospect'
};

export default StatusBadge; 